import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import { StatusForCompanyEnum } from 'api/enums'
import { updateWorkerStatusForCompany } from 'api/worker'
import { useOptimisticUpdate } from 'hooks/useOptimisticUpdate'
import { workerKeys } from './keys'
import { shiftWorkKey } from 'queries/shift/keys'
import { useContext } from 'react'
import { ShiftDetailContext } from 'pages/ShiftDetailPage/ShiftDetailProvider'
import { getShiftWork } from 'api/shift'
import { useWorkerProfile } from './useWorkerProfile'
import { useAuthContext } from 'components/auth'

type WorkerProfile = NonNullable<ReturnType<typeof useWorkerProfile>['data']>
type ShiftWork = Awaited<ReturnType<typeof getShiftWork>>

type UseUpdateWorkerStatusForCompanyMutationOptions = Partial<
  UseMutationOptions<
    Awaited<ReturnType<typeof updateWorkerStatusForCompany>>['data'],
    Error,
    WorkerStatusForCompanyRequest,
    [WorkerProfile, ShiftWork]
  >
>

type WorkerStatusForCompanyRequest = Parameters<
  typeof updateWorkerStatusForCompany
>[2]

const mapActionToStatus = {
  block: StatusForCompanyEnum.Blocked,
  favorite: StatusForCompanyEnum.Favorite,
  unfavorite: null,
  unblock: null,
}

export function useUpdateWorkerStatusForCompanyMutation(
  workerId: number,
  options: UseUpdateWorkerStatusForCompanyMutationOptions = {}
) {
  const { company } = useAuthContext()

  // From AssignedWorkerRating row
  const context = useContext(ShiftDetailContext)
  const shiftId = context?.shift?.id

  const optionsWithOptimisticUpdate = useOptimisticUpdate(options, [
    {
      queryKey: workerKeys.profile(workerId),
      updater: (data, previousState) => {
        if (previousState === undefined) {
          return previousState
        }

        return {
          ...previousState,
          worker: {
            ...previousState.worker,
            statusForCompany: mapActionToStatus[data.action],
          },
        }
      },
    },
    {
      queryKey: shiftWorkKey(shiftId ?? -1),
      updater: (data, previousState) => {
        if (!previousState) {
          return previousState
        }

        return {
          ...previousState,
          work: previousState.work.map((work) => {
            if (work.worker.id === workerId) {
              return {
                ...work,
                worker: {
                  ...work.worker,
                  statusForCompany: mapActionToStatus[data.action],
                },
              }
            }
            return work
          }),
        }
      },
    },
  ])

  return useMutation({
    mutationFn: async (data: WorkerStatusForCompanyRequest) => {
      if (!company?.id) {
        throw new Error('Company id not found')
      }

      await updateWorkerStatusForCompany(company.id, workerId, data)
    },
    ...optionsWithOptimisticUpdate,
  })
}
